import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CloseIcon from "@mui/icons-material/Close";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";

import {
  AddressFormActions,
  AddressFormFields,
  DoneButton,
  UseMyLocationButton,
  UseMyLocationError,
} from "./PhlebotomySearchPage.styles";
import { GEOLOCATION_POSITION_OPTIONS } from "./phlebotomySearchConstants";
import { US_ZIP_CODE_REGEX } from "./phlebotomyFormUtils";
import {
  createPlaceAutocompleteSessionToken,
  fetchAddressPredictions,
  fetchPlaceDetails,
  MIN_ADDRESS_AUTOCOMPLETE_CHARS,
} from "./phlebotomyAddressAutocompleteUtils";
import type {
  PlacePredictionOption,
  PlacePredictionsById,
} from "./phlebotomyAddressAutocompleteUtils";

export type PhlebotomySearchAddressSelection =
  | { type: "zip"; zip: string }
  | {
      type: "address" | "geolocation";
      lat: number;
      lng: number;
      zip?: string;
      /** Shown under Change location after search (address text or "Your current location"). */
      displayLabel: string;
    };

interface PhlebotomySearchAddressFormProps {
  onSubmit: (selection: PhlebotomySearchAddressSelection) => void;
  inline?: boolean;
  initialLocationValue?: string | null;
  initialSelection?: PhlebotomySearchAddressSelection | null;
}

const PhlebotomySearchAddressForm: React.FC<
  PhlebotomySearchAddressFormProps
> = ({
  onSubmit,
  inline = false,
  initialLocationValue = null,
  initialSelection = null,
}) => {
  const { t } = useTranslation();
  const places = useMapsLibrary("places");
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const predictionsByPlaceIdRef = useRef<PlacePredictionsById>(new Map());

  const [locationInput, setLocationInput] = useState(
    initialLocationValue ?? "",
  );
  const [streetOptions, setStreetOptions] = useState<PlacePredictionOption[]>(
    [],
  );
  const [selectedStreet, setSelectedStreet] =
    useState<PlacePredictionOption | null>(null);
  const [streetLookupError, setStreetLookupError] = useState<string | null>(
    null,
  );
  const [geolocationError, setGeolocationError] = useState<string | null>(null);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [hasFetchedStreetOptions, setHasFetchedStreetOptions] = useState(false);
  const [isStreetListOpen, setIsStreetListOpen] = useState(false);
  const [resolvingPlace, setResolvingPlace] = useState(false);
  const [isRequestingGeolocation, setIsRequestingGeolocation] = useState(false);

  const trimmedLocationInput = locationInput.trim();
  const isZipLikeInput = /^\d{0,10}$/.test(trimmedLocationInput);
  const hasStreetQuery =
    trimmedLocationInput.length >= MIN_ADDRESS_AUTOCOMPLETE_CHARS &&
    !isZipLikeInput;
  const hasUnchangedInitialAddress =
    initialSelection?.type === "address" &&
    trimmedLocationInput === initialSelection.displayLabel.trim();

  useEffect(() => {
    if (initialLocationValue) {
      setLocationInput(initialLocationValue);
    }
  }, [initialLocationValue]);

  useEffect(() => {
    if (!places) {
      sessionTokenRef.current = null;
      predictionsByPlaceIdRef.current.clear();
      return;
    }

    sessionTokenRef.current = createPlaceAutocompleteSessionToken(places);
  }, [places]);

  useEffect(() => {
    if (
      !places ||
      selectedStreet ||
      hasUnchangedInitialAddress ||
      !hasStreetQuery
    ) {
      setStreetOptions([]);
      setLoadingPredictions(false);
      setHasFetchedStreetOptions(false);
      // Keep the selected prediction so Search can still resolve lat/lng.
      if (!selectedStreet) {
        predictionsByPlaceIdRef.current.clear();
      }
      return;
    }

    let cancelled = false;
    setLoadingPredictions(true);
    setHasFetchedStreetOptions(false);
    const sessionToken =
      sessionTokenRef.current ?? new places.AutocompleteSessionToken();
    sessionTokenRef.current = sessionToken;

    const timeoutId = window.setTimeout(async () => {
      try {
        const { options, predictionsByPlaceId } = await fetchAddressPredictions(
          places,
          locationInput,
          sessionToken,
        );
        if (cancelled) return;

        predictionsByPlaceIdRef.current = predictionsByPlaceId;
        setStreetOptions(options);
        setLoadingPredictions(false);
        setHasFetchedStreetOptions(true);
      } catch {
        if (cancelled) return;
        predictionsByPlaceIdRef.current.clear();
        setStreetOptions([]);
        setLoadingPredictions(false);
        setHasFetchedStreetOptions(true);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    places,
    selectedStreet,
    locationInput,
    hasStreetQuery,
    hasUnchangedInitialAddress,
  ]);

  useEffect(() => {
    if (selectedStreet || hasUnchangedInitialAddress || !hasStreetQuery) {
      setIsStreetListOpen(false);
      return;
    }

    if (loadingPredictions || hasFetchedStreetOptions) {
      setIsStreetListOpen(true);
    }
  }, [
    selectedStreet,
    hasUnchangedInitialAddress,
    hasStreetQuery,
    loadingPredictions,
    hasFetchedStreetOptions,
  ]);

  const clearStreetSelection = () => {
    setSelectedStreet(null);
    setStreetLookupError(null);
  };

  const isAwaitingCoordinates = resolvingPlace || isRequestingGeolocation;

  const handleLocationInputChange = (value: string) => {
    setLocationInput(value);
    setStreetLookupError(null);
    setGeolocationError(null);
    if (!selectedStreet || value !== selectedStreet.description) {
      clearStreetSelection();
    }
  };

  const clearLocationInput = () => {
    setLocationInput("");
    clearStreetSelection();
    setGeolocationError(null);
    setIsStreetListOpen(false);
  };

  const canSubmit =
    US_ZIP_CODE_REGEX.test(locationInput.trim()) ||
    Boolean(selectedStreet) ||
    hasUnchangedInitialAddress;

  const handleUseMyLocation = () => {
    setStreetLookupError(null);
    setGeolocationError(null);

    if (!navigator.geolocation) {
      setGeolocationError(t("phlebotomySearch.errors.locationUnavailable"));
      return;
    }

    setIsRequestingGeolocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsRequestingGeolocation(false);
        setLocationInput("");
        clearStreetSelection();
        onSubmit({
          type: "geolocation",
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          displayLabel: t("phlebotomySearch.search.currentLocationLabel"),
        });
      },
      () => {
        setIsRequestingGeolocation(false);
        setGeolocationError(t("phlebotomySearch.errors.locationDenied"));
      },
      GEOLOCATION_POSITION_OPTIONS,
    );
  };

  const resolveSelectedPlace = async (
    option: PlacePredictionOption,
  ): Promise<{
    lat: number;
    lng: number;
    zip?: string;
    displayLabel: string;
  }> => {
    if (!places) {
      throw new Error("Places library unavailable");
    }

    const place = await fetchPlaceDetails(
      places,
      predictionsByPlaceIdRef.current,
      option.placeId,
    );

    sessionTokenRef.current = createPlaceAutocompleteSessionToken(places);
    predictionsByPlaceIdRef.current.clear();

    const { addressComponents, location, formattedAddress } = place;
    if (!location) {
      throw new Error("Unable to resolve place");
    }

    return {
      lat: location.lat(),
      lng: location.lng(),
      zip: addressComponents
        ?.find((component) => component.types.includes("postal_code"))
        ?.shortText?.slice(0, 5),
      displayLabel: formattedAddress || option.description,
    };
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStreetLookupError(null);
    setGeolocationError(null);

    const zip = locationInput.trim();
    if (US_ZIP_CODE_REGEX.test(zip)) {
      onSubmit({ type: "zip", zip: zip.slice(0, 5) });
      return;
    }

    if (hasUnchangedInitialAddress && initialSelection?.type === "address") {
      onSubmit(initialSelection);
      return;
    }

    if (selectedStreet) {
      setResolvingPlace(true);
      try {
        const place = await resolveSelectedPlace(selectedStreet);
        onSubmit({
          type: "address",
          lat: place.lat,
          lng: place.lng,
          zip: place.zip,
          displayLabel: place.displayLabel,
        });
      } catch {
        setStreetLookupError(t("phlebotomySearch.errors.addressNotFound"));
      } finally {
        setResolvingPlace(false);
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={inline ? { width: "100%" } : undefined}
    >
      <AddressFormFields
        sx={
          inline
            ? {
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                alignItems: "center",
                gap: 1,
              }
            : undefined
        }
      >
        <Autocomplete
          fullWidth
          options={streetOptions}
          filterOptions={(options) => options}
          loading={loadingPredictions}
          disabled={isAwaitingCoordinates}
          disableClearable={inline}
          value={selectedStreet}
          inputValue={locationInput}
          open={isStreetListOpen}
          openOnFocus={false}
          forcePopupIcon={false}
          noOptionsText={
            hasStreetQuery && hasFetchedStreetOptions
              ? t("phlebotomySearch.search.noAddressOptions")
              : null
          }
          onOpen={() => {
            if (
              hasStreetQuery &&
              !selectedStreet &&
              !hasUnchangedInitialAddress
            ) {
              setIsStreetListOpen(true);
            }
          }}
          onClose={() => setIsStreetListOpen(false)}
          getOptionLabel={(option) => option.description}
          isOptionEqualToValue={(option, value) =>
            option.placeId === value.placeId
          }
          onInputChange={(_event, value, reason) => {
            if (reason === "input" || reason === "clear") {
              handleLocationInputChange(value);
            }
          }}
          onChange={(_event, value) => {
            if (!value) {
              clearStreetSelection();
              return;
            }
            setSelectedStreet(value);
            setLocationInput(value.description);
            setStreetLookupError(null);
            setGeolocationError(null);
            setIsStreetListOpen(false);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.placeId}>
              <Typography component="span" variant="body1">
                {option.primaryText}
                {option.secondaryText ? (
                  <Typography
                    component="span"
                    variant="body1"
                    color="text.secondary"
                  >
                    {` ${option.secondaryText}`}
                  </Typography>
                ) : null}
              </Typography>
            </li>
          )}
          renderInput={(params) => {
            const { InputProps, inputProps, ...textFieldParams } = params;
            return (
              <TextField
                {...textFieldParams}
                autoFocus
                label={
                  inline
                    ? undefined
                    : `${t("phlebotomySearch.search.locationLabel")} *`
                }
                placeholder={
                  inline
                    ? t("phlebotomySearch.search.locationLabel")
                    : undefined
                }
                size={inline ? "small" : "medium"}
                sx={
                  inline
                    ? {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "22px",
                          minHeight: 40,
                        },
                      }
                    : undefined
                }
                error={Boolean(streetLookupError)}
                helperText={streetLookupError}
                InputProps={{
                  ...InputProps,
                  inputProps: {
                    ...inputProps,
                    ...(inline
                      ? {
                          "aria-label": t(
                            "phlebotomySearch.search.locationLabel",
                          ),
                        }
                      : {}),
                  },
                  startAdornment: inline ? (
                    <SearchIcon
                      sx={{
                        mr: 1,
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    />
                  ) : (
                    InputProps.startAdornment
                  ),
                  endAdornment: (
                    <>
                      {loadingPredictions || isAwaitingCoordinates ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {inline && locationInput && !isAwaitingCoordinates ? (
                        <IconButton
                          aria-label={t(
                            "phlebotomySearch.search.clearLocation",
                          )}
                          edge="end"
                          size="small"
                          onClick={clearLocationInput}
                          sx={{
                            color: (theme) => theme.palette.text.secondary,
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                      {InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            );
          }}
        />
        <UseMyLocationButton
          type="button"
          variant={inline ? "text" : "unity-outline-orange"}
          disabled={isAwaitingCoordinates}
          onClick={handleUseMyLocation}
          startIcon={
            isRequestingGeolocation ? (
              <CircularProgress color="inherit" size={18} />
            ) : undefined
          }
          sx={
            inline
              ? {
                  alignSelf: "center",
                  minWidth: 32,
                  minHeight: 32,
                  padding: 0,
                  color: (theme) => theme.palette.custom.redMedium,
                }
              : undefined
          }
          aria-label={
            inline ? t("phlebotomySearch.search.useMyLocation") : undefined
          }
        >
          {inline ? (
            <MyLocationIcon fontSize="small" />
          ) : (
            t("phlebotomySearch.search.useMyLocation")
          )}
        </UseMyLocationButton>
        {inline && (
          <DoneButton
            type="submit"
            variant="unity-primary"
            disabled={!canSubmit || isAwaitingCoordinates}
            sx={{
              gridColumn: "1 / -1",
              height: 40,
              minHeight: 40,
              width: "100%",
              paddingInline: 1,
            }}
          >
            {t("phlebotomySearch.search.doneButton")}
          </DoneButton>
        )}
        {geolocationError && (
          <UseMyLocationError
            variant="body2"
            role="alert"
            sx={inline ? { gridColumn: "1 / -1" } : undefined}
          >
            {geolocationError}
          </UseMyLocationError>
        )}
      </AddressFormFields>
      {!inline && (
        <AddressFormActions>
          <DoneButton
            type="submit"
            variant="unity-primary"
            disabled={!canSubmit || isAwaitingCoordinates}
          >
            {t("phlebotomySearch.search.doneButton")}
          </DoneButton>
        </AddressFormActions>
      )}
    </form>
  );
};

export default PhlebotomySearchAddressForm;
